// Multiplication Garden: plant seeds in rows to see arrays. Turn the plot and it is the
// same number. Grown plots stay as a little garden for the rest of the round.

import { h, clear } from '@kit/dom';
import { makeSurface, roundRect } from '@kit/canvas';
import type { GameModule, GameContext } from '@shell/types';
import { makeArray, type ArrayProblem } from '@problems/arrays';
import { makeNumpad, makeChoices } from '@games/common/numpad';

const PLOTS = 6;

function mount(host: HTMLElement, ctx: GameContext) {
  let n = 0;
  let level = ctx.currentLevel();
  let p: ArrayProblem = makeArray(level, ctx.rng);
  let planted = 0;              // rows planted so far
  let rotated = false;
  let wrongs = 0, hinted = false, locked = false;
  let attempts = 0, correct = 0;
  const garden: { rows: number; cols: number }[] = [];
  const t0 = performance.now();
  let tProblem = performance.now();

  const head = h('div.row', { style: { justifyContent: 'space-between' } }, h('span.muted', null, 'Plot ', h('strong', null, '1'), ` of ${PLOTS}`), h('span.muted.small', null, ''));
  const packet = h('div.center', { style: { fontWeight: '900', fontSize: '22px', minHeight: '32px' } });
  const stage = h('div');
  const controls = h('div.stack', { style: { gap: '8px' } });
  host.append(head, packet, stage, controls);

  const S = makeSurface(stage, { aspect: 0.82, draw: false, onResize: draw });

  function setup() {
    level = ctx.currentLevel();
    p = makeArray(level, ctx.rng);
    planted = p.plant ? 0 : p.rows;
    rotated = false; wrongs = 0; hinted = false; locked = false;
    tProblem = performance.now();
    (head.querySelector('strong') as HTMLElement).textContent = String(n + 1);
    (head.lastElementChild as HTMLElement).textContent = p.kind === 'area' ? 'Square units' : p.kind === 'split' ? 'Split the plot' : 'Seed packet';
    packet.textContent = p.text;
    renderControls();
    draw();
  }

  function renderControls() {
    clear(controls);
    if (p.plant && planted < maxRows()) {
      controls.append(h('button.btn.primary.block', { onclick: plantRow }, `Plant a row of ${p.cols}`));
      if (p.kind === 'missing-rows' || p.kind === 'division') {
        controls.append(h('button.btn.block', { disabled: planted === 0, onclick: () => answer(planted) }, planted ? `That's it: ${planted} row${planted === 1 ? '' : 's'}` : 'Plant rows until the seeds run out'));
      }
      return;
    }
    if (p.kind === 'missing-rows' || p.kind === 'division') {
      controls.append(h('button.btn.primary.block', { onclick: () => answer(planted) }, `That's it: ${planted} rows`));
      return;
    }
    const prompt = p.kind === 'area' ? 'How many square units?' : 'How many seeds in all?';
    controls.append(h('p.center.muted.small', null, prompt));
    if (p.choices) controls.append(makeChoices(p.choices, (v) => answer(Number(v))).el);
    else controls.append(makeNumpad({ onEnter: answer }).el);
  }

  function maxRows(): number {
    // for the counting kinds she may overshoot by a row or two, then learns from the count
    return p.kind === 'missing-rows' || p.kind === 'division' ? p.rows + 2 : p.rows;
  }

  function plantRow() {
    if (locked || planted >= maxRows()) return;
    planted++;
    ctx.sound('pop');
    if (p.kind === 'missing-rows' || p.kind === 'division') ctx.say(`${planted * p.cols} seeds planted so far.`);
    else if (planted < p.rows) ctx.say(`${planted} of ${p.rows} rows. That is ${planted * p.cols} seeds so far.`);
    else ctx.say(`All ${p.rows} rows planted. How many seeds?`);
    renderControls();
    draw();
  }

  function answer(v: number) {
    if (locked) return;
    const ms = performance.now() - tProblem;
    attempts++;
    if (v === p.answer) {
      correct++; locked = true;
      ctx.report({ skill: 'mult_div', level, correct: true, hintUsed: hinted, ms, problemId: `array:${p.kind}:${p.rows}x${p.cols}` });
      ctx.sound('ding');
      if (p.kind === 'total' || p.kind === 'area') {
        ctx.say(`${p.rows} x ${p.cols} = ${p.answer}. Turn it: ${p.cols} rows of ${p.rows} is also ${p.answer}.`);
        rotated = true; draw();
      } else if (p.kind === 'split') {
        ctx.say(`${p.rows} x 10 + ${p.rows} x ${p.split!.b} = ${p.rows * 10} + ${p.rows * p.split!.b} = ${p.answer}.`);
      } else {
        ctx.say(`${p.rows} rows of ${p.cols} make ${p.rows * p.cols}. ${p.rows * p.cols} divided by ${p.cols} is ${p.rows}.`);
        planted = p.rows; draw();
      }
      garden.push({ rows: p.rows, cols: p.cols });
      ctx.keep('plot', { rows: p.rows, cols: p.cols });
      next(1500);
    } else {
      wrongs++;
      ctx.report({ skill: 'mult_div', level, correct: false, hintUsed: hinted, ms, problemId: `array:${p.kind}:${p.rows}x${p.cols}` });
      ctx.sound('buzz');
      hinted = true;
      if (wrongs >= 2) {
        locked = true;
        ctx.say(`It is ${p.answer}. ${p.hint}`);
        planted = p.rows; draw();
        garden.push({ rows: p.rows, cols: p.cols });
        next(1800);
      } else {
        ctx.say(`Not ${v}. ${p.hint}`);
        if ((p.kind === 'missing-rows' || p.kind === 'division') && planted !== p.rows) { locked = false; }
      }
    }
  }

  function next(delay: number) {
    n++;
    setTimeout(() => {
      if (n >= PLOTS) {
        if (attempts === correct) ctx.charm('charm.garden');
        ctx.end({ attempts, correct, perfect: attempts === correct, durationMs: performance.now() - t0, notes: [`Plots grown: ${garden.length}`] });
      } else setup();
    }, delay);
  }

  function drawPlot(c: CanvasRenderingContext2D, x: number, y: number, w: number, hgt: number, rows: number, cols: number, plantedRows: number, highlightSplit?: number) {
    c.fillStyle = '#8a5a3b'; roundRect(c, x, y, w, hgt, 8); c.fill();
    const cw = w / cols, ch = hgt / rows, r = Math.min(cw, ch) * 0.3;
    for (let i = 0; i < rows; i++) for (let j = 0; j < cols; j++) {
      const cx = x + (j + 0.5) * cw, cy = y + (i + 0.5) * ch;
      if (i < plantedRows) {
        c.fillStyle = highlightSplit !== undefined && j >= highlightSplit ? '#f5c542' : '#5fbf6a';
        c.beginPath(); c.arc(cx, cy, r, 0, 7); c.fill();
        c.fillStyle = '#2e7d4f'; c.beginPath(); c.ellipse(cx + r * 0.5, cy - r * 0.9, r * 0.45, r * 0.25, 0.6, 0, 7); c.fill();
      } else {
        c.fillStyle = 'rgba(0,0,0,.18)'; c.beginPath(); c.arc(cx, cy, r * 0.45, 0, 7); c.fill();
      }
    }
    if (highlightSplit !== undefined) { c.strokeStyle = '#fff'; c.lineWidth = 3; c.setLineDash([6, 6]); c.beginPath(); c.moveTo(x + highlightSplit * cw, y); c.lineTo(x + highlightSplit * cw, y + hgt); c.stroke(); c.setLineDash([]); }
  }

  function draw() {
    const c = S.ctx, W = S.w, H = S.h;
    c.fillStyle = '#d9efc8'; c.fillRect(0, 0, W, H);
    const rows = rotated ? p.cols : p.rows, cols = rotated ? p.rows : p.cols;
    const plantedRows = rotated ? rows : planted;
    const area = { x: W * 0.06, y: H * 0.05, w: W * 0.88, h: H * 0.6 };
    const cell = Math.min(area.w / cols, area.h / rows);
    const pw = cell * cols, ph = cell * rows;
    drawPlot(c, area.x + (area.w - pw) / 2, area.y + (area.h - ph) / 2, pw, ph, rows, cols, plantedRows, p.kind === 'split' ? p.split!.a : undefined);
    // labels
    c.fillStyle = '#2a2a2e'; c.font = `800 ${Math.round(Math.min(16, W * 0.04))}px Nunito, sans-serif`; c.textAlign = 'center'; c.textBaseline = 'middle';
    c.fillText(`${plantedRows} row${plantedRows === 1 ? '' : 's'} of ${cols}${p.kind === 'split' ? ` (10 + ${p.split!.b})` : ''}`, W / 2, area.y + area.h + 14);
    // the garden so far
    const gy = H * 0.74, gh = H * 0.22;
    c.fillStyle = 'rgba(0,0,0,.05)'; c.fillRect(0, gy - 6, W, gh + 12);
    c.fillStyle = '#6a6a72'; c.font = `700 12px Nunito, sans-serif`; c.textAlign = 'left'; c.fillText('Your garden', 10, gy + 4);
    const slot = (W - 20) / PLOTS;
    garden.forEach((g, i) => {
      const s = Math.min(slot * 0.85, gh - 18);
      const cw = s / Math.max(g.cols, g.rows), ph2 = cw * g.rows, pw2 = cw * g.cols;
      drawPlot(c, 10 + i * slot + (slot - pw2) / 2, gy + 14 + (s - ph2) / 2, pw2, ph2, g.rows, g.cols, g.rows);
    });
  }

  setup();
  return { destroy() { S.destroy(); clear(host); } };
}

const mod: GameModule = {
  id: 'math.multiplication-garden',
  title: 'Multiplication Garden',
  blurb: 'Plant seeds in rows and see multiplication grow. Turn the plot: same number.',
  category: 'math',
  skills: ['mult_div'],
  levelRange: [1, 10],
  unlock: { stars: 11 },
  mount,
};
export default mod;
